import {
    ChangeDetectionStrategy,
    Component,
    Inject,
    OnInit,
} from '@angular/core';
import {
    UntypedFormArray,
    UntypedFormBuilder,
    UntypedFormGroup,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { take } from 'rxjs/operators';

import { Validators } from '@shared/forms/validators/validators';

import { SurveyDialogData } from './models/survey-dialog-data.model';
import { SurveyQuestionOptionType } from './models/survey-question-option-type.enum';
import { SurveyType, SurveyTypeName } from './models/survey-type.enum';
// Updated import to include SurveyType
import { UserSurvey } from './models/user-survey.model';
import { SurveyDataService } from './services/survey.dataservice';

@Component({
    templateUrl: './survey.component.html',
    styleUrls: ['./survey.component.scss'],
    providers: [UntypedFormBuilder],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class SurveyComponent implements OnInit {
    formGroup: UntypedFormGroup;
    surveyQuestionOptionType: any = SurveyQuestionOptionType;
    surveyType: string = '';
    name: string = '';
    classSessionId: string = '';

    constructor(
        private formBuilder: UntypedFormBuilder,
        private dialogRef: MatDialogRef<SurveyComponent>,
        private surveyDataService: SurveyDataService,
        @Inject(MAT_DIALOG_DATA) public data: SurveyDialogData,
    ) {}

    ngOnInit(): void {
        this.classSessionId = this.data.classSessionId;
        this.formGroup = this.formBuilder.group({
            surveyId: [this.data.survey.id],
            userAnswers: this.formBuilder.array(
                this.data.survey.questions.map((surveyQuestion) =>
                    this.formBuilder.group({
                        question: [surveyQuestion.question],
                        surveyQuestionId: [surveyQuestion.id],
                        surveyQuestionOptionId: [null, Validators.required()],
                    }),
                ),
            ),
            comment: [null, [Validators.maxLength(500)]],
        });

        if (this.data.survey?.type === SurveyType.FacilitatorSurvey) {
            this.surveyType = SurveyTypeName.FacilitatorSurvey;
            this.name = this.data.survey.name;
        } else if (this.data.isClassSurvey) {
            this.surveyType = SurveyTypeName.ClassAssessment;
            this.name = this.data.title;
        } else {
            this.surveyType = SurveyTypeName.SelfAssessment;
        }
    }

    get userAnswers(): UntypedFormArray {
        return this.formGroup.get('userAnswers') as UntypedFormArray;
    }

    isFacilitatorSurvey(): boolean {
        return this.data.survey?.type === SurveyType.FacilitatorSurvey;
    }

    getQuestionOptions(questionIndex: number) {
        if (!this.isFacilitatorSurvey()) {
            return this.data.survey.questionOptions;
        }

        if (questionIndex === 0) {
            return this.data.survey.questionOptions.filter(
                (option) =>
                    option.name === 'Very well' ||
                    option.name === 'Okay' ||
                    option.name === 'Not well',
            );
        }

        if (questionIndex === 1) {
            return this.data.survey.questionOptions.filter(
                (option) =>
                    option.name === 'Very engaged' ||
                    option.name === 'Somewhat engaged' ||
                    option.name === 'Not engaged',
            );
        }

        if (questionIndex === 2) {
            return this.data.survey.questionOptions.filter(
                (option) => option.name === 'Yes' || option.name === 'No',
            );
        }

        return [];
    }

    skip(): void {
        const userSurvey = this.formGroup.value as UserSurvey;
        userSurvey.comment = null;
        userSurvey.userAnswers = []; // Changed from null to empty array to protect API model serialization requirements
        userSurvey.classSessionId = this.classSessionId;

        this.surveyDataService
            .saveUserSurvey(userSurvey)
            .pipe(take(1))
            .subscribe();
        this.dialogRef.close();
    }

    save(): void {
        if (this.formGroup.valid) {
            const userSurvey = this.formGroup.value as UserSurvey;
            userSurvey.classSessionId = this.classSessionId;

            this.surveyDataService
                .saveUserSurvey(userSurvey)
                .pipe(take(1))
                .subscribe();
            this.dialogRef.close();
        }
    }
}
